/**
 * Download Queue
 *
 * Manages a prioritized queue of media downloads with
 * concurrent download limits and retry logic.
 */

import { PlaylistItem } from '~/services/scheduler/types';
import { detectPlatform } from '~/services/platform/utils/detectPlatform';
import {
  DownloadTask,
  DownloadProgressCallback,
  DownloadCompleteCallback,
  DownloadErrorCallback,
  IDownloadQueue,
} from './types';

/**
 * Max concurrent downloads.
 *
 * 2026-05-24 — Em Tizen consumer (UN50U8600F, Tizen 7.7) baixamos via
 * `response.blob()` direto pra contornar bug de stream corruption (ver
 * downloadWithProgress). Esse caminho aloca o blob inteiro em RAM antes
 * de escrever no FileSystem. Com 2 downloads paralelos de 20MB+, o
 * WebView consumer estourava memória e crashava o app (TV voltava
 * pra fonte HDMI/programação default).
 *
 * Fix: serializar em Tizen (1 por vez), mantém 2 em browser/Android/webOS
 * onde o getReader streaming não tem esse problema.
 */
function getMaxConcurrent(): number {
  return detectPlatform() === 'tizen' ? 1 : 2;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5000;
// Circuit-breaker: nº de falhas nativas SEM nenhum sucesso que desliga o
// native download na sessão (fallback pro blob/writeBlob). ~1 item de retries.
const NATIVE_BREAKER_THRESHOLD = 4;

/**
 * Formats bytes to human readable string.
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

class DownloadQueue implements IDownloadQueue {
  private queue: DownloadTask[] = [];
  private activeDownloads = 0;
  /**
   * Ids dos itens EM DOWNLOAD agora (já saíram da fila). O `enqueue` deduplicava só
   * contra a fila pendente, então dois prefetches em sequência (ex.: syncs 0,4s
   * apartados) baixavam o MESMO arquivo em paralelo — medido no tablet em
   * 2026-10-15 (`Active: 2` pro mesmo conteúdo, a 130 KB/s cada, engasgando o
   * stream que tocava aquele mesmo vídeo da rede). Agora o item ativo também conta.
   */
  private activeTasks = new Map<string, DownloadTask>();
  private progressCallbacks: Set<DownloadProgressCallback> = new Set();

  // Callbacks for the MediaStore to hook into
  public onComplete: DownloadCompleteCallback | null = null;
  public onError: DownloadErrorCallback | null = null;

  // Native transfer hook (Tizen). When set, processTask downloads via the
  // native download manager (rede→disco off-thread) instead of fetch→blob,
  // eliminating the ~20s writeBlob freeze. Throws on failure → normal retry.
  public nativeDownloader:
    | ((
        item: PlaylistItem,
        onProgress: (progress: number) => void,
        signal: AbortSignal,
      ) => Promise<void>)
    | null = null;

  // Completion callback for the native path (no blob). Called after a
  // successful native transfer so the MediaStore can update cacheStatus.
  public onNativeComplete:
    | ((item: PlaylistItem) => Promise<void> | void)
    | null = null;

  // Circuit-breaker state for the native path (see processTask).
  private nativeSuccesses = 0;
  private nativeFailures = 0;
  private nativeDisabled = false;

  /**
   * Quando true, o caminho nativo é OBRIGATÓRIO: o circuit-breaker NUNCA abre e a
   * fila NUNCA cai no fallback blob/fetch-no-renderer. Usado no EMBEDDED, onde o
   * blob (baixar o arquivo inteiro DENTRO do renderer 32-bit) vaza VA → renderer-kill
   * (a "bomba-relógio"). Falha nativa → retry nativo → eventual onError, mas JAMAIS
   * blob. Frota/browser/Tizen continuam com o breaker → blob (não vazam por lá).
   */
  public nativeMandatory = false;

  /**
   * Adds an item to the download queue.
   */
  enqueue(item: PlaylistItem, priority = 0): void {
    if (this.activeTasks.has(item.id)) {
      console.log(`[DownloadQueue] ⏭️ Já em download, ignorando: ${item.name}`);
      return;
    }
    // Check if already in queue
    const existing = this.queue.find((t) => t.item.id === item.id);
    if (existing) {
      // Update priority if higher
      if (priority > existing.priority) {
        existing.priority = priority;
        this.sortQueue();
        console.log(
          `[DownloadQueue] 📈 Priority updated: ${item.name} -> ${priority}`,
        );
      }
      return;
    }

    const task: DownloadTask = {
      item,
      priority,
      retryCount: 0,
      abortController: new AbortController(),
    };

    this.queue.push(task);
    this.sortQueue();
    this.processQueue();

    console.log(`[DownloadQueue] ➕ Enqueued: ${item.name}`);
    console.log(
      `[DownloadQueue]    └─ Size: ${formatBytes(item.fileSize)}, Priority: ${priority}, Queue: ${this.queue.length}`,
    );
  }

  /**
   * Cancels a download.
   */
  cancel(itemId: string): void {
    // Em download agora: aborta o fetch (cai no AbortError do processTask, que
    // encerra sem onComplete/onError e libera a vaga). Antes, cancelar um item ativo
    // era no-op — só a fila pendente era olhada.
    const active = this.activeTasks.get(itemId);
    if (active) {
      active.abortController.abort();
      console.log(
        `[DownloadQueue] ⏹️ Cancelled (em download): ${active.item.name}`,
      );
    }
    const index = this.queue.findIndex((t) => t.item.id === itemId);
    if (index !== -1) {
      const task = this.queue[index];
      task.abortController.abort();
      this.queue.splice(index, 1);
      console.log(
        `[DownloadQueue] ⏹️ Cancelled: ${task.item.name}, Remaining queue: ${this.queue.length}`,
      );
    }
  }

  /**
   * Cancels all downloads.
   */
  cancelAll(): void {
    const count = this.queue.length + this.activeTasks.size;
    this.activeTasks.forEach((task) => task.abortController.abort());
    this.queue.forEach((task) => task.abortController.abort());
    this.queue = [];
    console.log(`[DownloadQueue] 🛑 Cancelled ALL: ${count} downloads aborted`);
  }

  /**
   * Registers a callback for download progress.
   */
  onProgress(callback: DownloadProgressCallback): () => void {
    this.progressCallbacks.add(callback);
    return () => this.progressCallbacks.delete(callback);
  }

  /**
   * Gets the current queue length.
   */
  getQueueLength(): number {
    return this.queue.length;
  }

  /**
   * Gets active download count.
   */
  getActiveDownloads(): number {
    return this.activeDownloads;
  }

  /**
   * Checks if an item is in the queue.
   */
  isInQueue(itemId: string): boolean {
    return (
      this.activeTasks.has(itemId) ||
      this.queue.some((t) => t.item.id === itemId)
    );
  }

  /**
   * Sorts the queue by priority (descending).
   */
  private sortQueue(): void {
    this.queue.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Notifies progress callbacks.
   */
  private notifyProgress(itemId: string, progress: number): void {
    this.progressCallbacks.forEach((cb) => {
      try {
        cb(itemId, progress);
      } catch (err) {
        console.error('[DownloadQueue] Progress callback error:', err);
      }
    });
  }

  /**
   * Processes the queue, starting downloads if slots are available.
   *
   * 2026-05-28 — Em Tizen, cede ao event loop entre downloads via
   * `setTimeout(0)` em vez de chamar `processQueue()` em cascata
   * sincronizada. Sintoma reportado (Renato print): app trava na virada do
   * 3º vídeo, controle remoto para de responder ("clico seta pra baixo e
   * não aparece o painelzinho"). Diagnóstico: thread JS congelado por
   * cadeia consecutiva de `cache.store()` + `validateChecksum()` + leitura
   * de blob — sem yield, o WebView nunca repaint, nunca processa input,
   * e a OS Tizen mata o app por janela de input perdida.
   *
   * `setTimeout(0)` dá ~1 tick pro GC reclamar o blob anterior (40 MB +)
   * e pro WebView processar pending events antes do próximo IDB write.
   */
  private processQueue(): void {
    const maxConcurrent = getMaxConcurrent();
    while (this.activeDownloads < maxConcurrent && this.queue.length > 0) {
      const task = this.queue.shift();
      if (!task) break;

      this.activeDownloads++;
      this.activeTasks.set(task.item.id, task);
      this.processTask(task).finally(() => {
        this.activeDownloads--;
        this.activeTasks.delete(task.item.id);
        // Yield ao event loop antes do próximo download. Em Tizen Consumer
        // (heap limitada), isso é a diferença entre app vivo e OOM.
        setTimeout(() => this.processQueue(), 0);
      });
    }
  }

  /**
   * Processes a single download task.
   */
  private async processTask(task: DownloadTask): Promise<void> {
    const { item, abortController } = task;
    // B3 fix (2026-06-08): preserva o startedAt do PRIMEIRO attempt. Retries
    // reusam o mesmo task object (re-enqueue na linha ~295), então o reset
    // incondicional anterior fazia o durationMs medir só o tempo desde o último
    // retry — escondendo lentidão acumulada (attempts falhados + delays de
    // backoff) na telemetria de "downloads lentos". Agora durationMs reflete o
    // tempo total desde o 1º attempt.
    if (task.startedAt == null) {
      task.startedAt = Date.now();
    }

    try {
      console.log(`[DownloadQueue] ⬇️ Starting: ${item.name}`);
      console.log(`[DownloadQueue]    └─ URL: ${item.url}`);
      console.log(
        `[DownloadQueue]    └─ Expected size: ${formatBytes(item.fileSize)}`,
      );

      // Native transfer path (Tizen): download rede→disco off-thread, sem
      // Blob e sem writeBlob síncrono. Mantém a fila/concorrência/retry desta
      // DownloadQueue; só troca o mecanismo de transferência. Um throw cai no
      // catch abaixo (retry). Nada de blob → completion própria.
      //
      // Circuit-breaker: se as primeiras NATIVE_BREAKER_THRESHOLD tentativas
      // nativas falharem SEM nenhum sucesso (ex.: firmware onde tizen.download
      // existe mas não baixa), desliga o native na sessão e cai pro caminho
      // blob/writeBlob — protege TVs onde o native está presente-mas-quebrado
      // (um `tizen.download` ausente já cai no fallback via shouldUseNativeDownload).
      if (this.nativeDownloader && !this.nativeDisabled) {
        try {
          await this.nativeDownloader(
            item,
            (progress) => this.notifyProgress(item.id, progress),
            abortController.signal,
          );
          this.nativeSuccesses++;
        } catch (nativeErr) {
          if ((nativeErr as Error)?.name !== 'AbortError') {
            this.nativeFailures++;
            if (
              !this.nativeMandatory && // embedded: NUNCA cai no blob (bomba de VA)
              this.nativeSuccesses === 0 &&
              this.nativeFailures >= NATIVE_BREAKER_THRESHOLD
            ) {
              this.nativeDisabled = true;
              console.warn(
                `[DownloadQueue] 🔌 Native download circuit-breaker ABERTO após ${this.nativeFailures} falhas sem sucesso — fallback pro caminho blob/writeBlob nesta sessão`,
              );
            }
          }
          throw nativeErr; // retry existente age; se breaker abriu, a próxima usa writeBlob
        }
        this.notifyProgress(item.id, 100);
        if (this.onNativeComplete) {
          await this.onNativeComplete(item);
        }
        const nativeMs = Date.now() - (task.startedAt || 0);
        console.log(
          `[DownloadQueue] ✅ Completed (native): ${item.name} in ${(nativeMs / 1000).toFixed(1)}s`,
        );
        return;
      }

      // `let` em vez de `const` pra que possamos nullificar a referência
      // explicitamente após onComplete. Em Tizen Consumer, segurar o blob
      // (40 MB+) na closure de processTask até a função retornar gera
      // pressão de memória cumulativa: durante o download do PRÓXIMO vídeo,
      // o blob anterior ainda está vivo + o novo blob está sendo alocado +
      // o WebView está fazendo crypto.subtle.digest do novo, somando ~120 MB
      // de JS heap só pra dois vídeos consecutivos. Crash típico no 3º.
      // (Renato 2026-05-28)
      let blob: Blob | null = await this.downloadWithProgress(
        item.url,
        abortController.signal,
        (progress) => {
          this.notifyProgress(item.id, progress);
        },
      );

      this.notifyProgress(item.id, 100);

      // Captura métricas ANTES de liberar `blob` pra que o log abaixo não
      // precise mais da referência.
      const blobSize = blob.size;

      // Notify completion.
      //
      // ⚠️ AWAIT obrigatório: onComplete em media.store é `async` (chama
      // cache.store que pode rejeitar — IndexedDB QuotaExceeded, Tizen
      // filesystem TypeError, checksum mismatch, etc). Sem await, a rejeição
      // vira unhandled silenciosa e o item fica preso em status='pending':
      // cacheStatus nunca atualiza pra 'cached' nem pra 'error' → operador
      // não tem telemetria do que falhou, retry nunca dispara, prefetchAll
      // continua tentando enfileirar item que pensa estar pendente.
      // Se onComplete throw, a exception entra no catch block abaixo,
      // que aciona retry → eventual onError → status='error' com failedAt
      // (graças ao novo cooldown introduzido em media.store).
      if (this.onComplete) {
        try {
          await this.onComplete(item, blob);
        } finally {
          // Libera explicitamente a referência local. Após cache.store(),
          // o blob já foi persistido no IndexedDB/filesystem; manter a
          // referência aqui só atrasa o GC. Esse null hint é especialmente
          // crítico em Tizen Consumer 9.0 onde a heap WebView é apertada.
          blob = null;
        }
      } else {
        blob = null;
      }

      const durationMs = Date.now() - (task.startedAt || 0);
      const durationSec = (durationMs / 1000).toFixed(1);
      const speedBps = blobSize / (durationMs / 1000);
      const speedFormatted = formatBytes(speedBps) + '/s';

      console.log(`[DownloadQueue] ✅ Completed: ${item.name}`);
      console.log(
        `[DownloadQueue]    └─ Size: ${formatBytes(blobSize)}, Time: ${durationSec}s, Speed: ${speedFormatted}`,
      );
      console.log(
        `[DownloadQueue]    └─ Active: ${this.activeDownloads}, Queue remaining: ${this.queue.length}`,
      );
    } catch (err) {
      // Handle abort
      if ((err as Error).name === 'AbortError') {
        console.log(`[DownloadQueue] ⏹️ Aborted: ${item.name}`);
        return;
      }

      const errorMsg = (err as Error).message || 'Unknown error';
      console.error(`[DownloadQueue] ❌ Failed: ${item.name}`);
      console.error(`[DownloadQueue]    └─ Error: ${errorMsg}`);

      // Retry logic
      if (task.retryCount < MAX_RETRIES) {
        task.retryCount++;
        task.abortController = new AbortController();

        const delayMs = RETRY_DELAY_MS * task.retryCount;
        console.log(
          `[DownloadQueue] 🔄 Retrying: ${item.name} (attempt ${task.retryCount + 1}/${MAX_RETRIES + 1}) in ${delayMs / 1000}s`,
        );

        // Re-enqueue with delay
        setTimeout(() => {
          this.queue.push(task);
          this.sortQueue();
          this.processQueue();
        }, delayMs);
      } else {
        // Max retries exceeded
        console.error(`[DownloadQueue] 💀 Max retries exceeded: ${item.name}`);
        if (this.onError) {
          this.onError(item, errorMsg);
        }
      }
    }
  }

  /**
   * Downloads a file with progress tracking.
   *
   * Strategy:
   *   - Tizen → `response.blob()` direto. Em Tizen 7.7 consumer (UN50U8600F)
   *     o caminho streaming (getReader + new Blob(chunks)) produz blobs cujo
   *     SHA-256 não bate com o checksum do server, mesmo recebendo o número
   *     correto de bytes. Confirmado via overlay com expected vs calculated
   *     em 3+ vídeos diferentes (Samsung-Galaxy, Pepsi, Chanel).
   *     Perda: progress só dispara em 0 → 100, sem grão intermediário.
   *   - Outras plataformas → streaming reader (mantém progress fino).
   *
   * Não usar window.tizen direto aqui (regra do projeto — platform
   * decoupling). Vamos por `detectPlatform()` que é a abstração canônica.
   */
  private async downloadWithProgress(
    url: string,
    signal: AbortSignal,
    onProgress: (progress: number) => void,
  ): Promise<Blob> {
    // `cache: 'reload'` ignora a cópia do cache HTTP e grava a resposta nova. O R2 não
    // manda `Vary: Origin`: uma cópia baixada antes SEM CORS (um <img> comum, o admin
    // no mesmo site) fazia este fetch CORS falhar com "Failed to fetch" e o item ficava
    // em cacheStatus=error — medido no player web 1.0.478 (2026-10-19).
    const response = await fetch(url, { signal, cache: 'reload' });

    if (!response.ok) {
      // 🔴 Drena antes de lançar. O caminho de sucesso consome o corpo inteiro
      // (blob no Tizen, reader nas demais), mas o de ERRO largava a `Response`
      // sem ler nem cancelar — e aqui o corpo de erro tem bytes de verdade
      // (XML do S3/R2, HTML do nginx). Segurar o data pipe do Chromium reserva
      // ~2 MB de address space por requisição no renderer 32-bit, que nunca
      // voltam: é a causa-raiz do renderer-kill medida no Tab A7 em 2026-09-18
      // (vide commit 683464d). Com retry (3×) e presigned expirada isso repete.
      await response.body?.cancel().catch(() => undefined);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const platform = detectPlatform();
    const contentLength = Number(response.headers.get('Content-Length')) || 0;
    const contentType =
      response.headers.get('Content-Type') || 'application/octet-stream';

    if (platform === 'tizen') {
      // Direct blob — bypass the streaming reader corruption observed on
      // Tizen 7.7 consumer TVs.
      onProgress(1); // Show "downloading" state in the UI immediately
      const blob = await response.blob();
      onProgress(100);

      if (contentLength > 0 && blob.size !== contentLength) {
        console.warn(
          `[DownloadQueue] ⚠️ Tizen blob size mismatch with Content-Length: expected ${contentLength}B, got ${blob.size}B`,
        );
      }

      // Tizen sometimes drops the Content-Type — patch it if needed so
      // downstream (Blob URL, video player) gets the right MIME.
      if (!blob.type && contentType) {
        return new Blob([blob], { type: contentType });
      }
      return blob;
    }

    // Streaming path — used by browser, Android (Capacitor), webOS.
    const reader = response.body?.getReader();

    if (!reader) {
      throw new Error('No response body');
    }

    const chunks: BlobPart[] = [];
    let receivedBytes = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      receivedBytes += value.length;

      // Calculate progress
      if (contentLength > 0) {
        const progress = Math.round((receivedBytes / contentLength) * 100);
        onProgress(progress);
      }
    }

    const blob = new Blob(chunks, { type: contentType });
    return blob;
  }
}

// Export singleton instance
const downloadQueue = new DownloadQueue();
export default downloadQueue;
